import { Filter } from '../../model/Filter';
import { toLocalDate, toJsDate } from '../../model/DateConversion';
import { PickerState, createInitialPickerState } from '../../model/datePicker/pickerState';
import { LocalDate, DateRange, YearMonth, RangePreset } from '../../model/datePicker/types';
import { monthOf, shiftYearMonth } from '../../model/datePicker/dateHelpers';
import { rangeFromPreset } from '../../model/datePicker/rangeFromPreset';
import { AppliedSelection } from './DatePickerPanel';
import { t } from '../../i18n';

// Puente entre el modelo de filtro existente (Date, from/to/compareFrom/
// compareTo) y el del picker nuevo (LocalDate, presets + rango derivado).
// La conversión Date⇄LocalDate en sí vive en model/DateConversion — la
// capa de query (ProductSales.ts, Historical.ts) también la necesita y no
// debe depender de components/.
const panelMonthFor = (range: DateRange): YearMonth => shiftYearMonth(monthOf(range.end), -1);

// El preset guardado solo vale si hoy sigue generando exactamente el rango A
// guardado — un "2w" aplicado ayer ya no describe esas fechas, y marcarlo
// en la barra lateral mentiría sobre lo que muestra el calendario.
const restoredRangePreset = (preset: RangePreset | undefined, a: DateRange, today: LocalDate): RangePreset => {
  if(!preset || preset === 'custom') {
    return 'custom';
  }
  const expected = rangeFromPreset(preset, today);
  return expected.start === a.start && expected.end === a.end ? preset : 'custom';
};

// A reabre con su preset si el filtro lo trae y sigue vigente (ver
// restoredRangePreset); si no — enlaces antiguos, o el preset ya no cuadra —
// en modo manual. comparePreset también se persiste (filterPatchFromApplied),
// así que si el filtro trae una regla derivada la reconstruimos tal cual, en
// vez de caer en 'custom'.
const pickerStateFromFilter = (filter: Filter, today: LocalDate): PickerState => {
  const base = createInitialPickerState(today);
  if(!filter.from || !filter.to) {
    return base;
  }
  const a: DateRange = { start: toLocalDate(filter.from), end: toLocalDate(filter.to) };
  const rangePreset = restoredRangePreset(filter.rangePreset, a, today);
  const comparePreset = filter.comparePreset ?? 'custom';
  if(comparePreset !== 'custom') {
    return { ...base, a, rangePreset, comparePreset, panelMonth: panelMonthFor(a) };
  }
  if(!filter.compareFrom || !filter.compareTo) {
    return { ...base, a, rangePreset, panelMonth: panelMonthFor(a) };
  }
  const manualB: DateRange = { start: toLocalDate(filter.compareFrom), end: toLocalDate(filter.compareTo) };
  return {
    ...base,
    a,
    rangePreset,
    comparePreset: 'custom',
    manualB,
    panelMonth: panelMonthFor(a),
    panelBMonth: panelMonthFor(manualB)
  };
};

// El corte de día parcial se traslada tal cual — Fase 6 lo consume en la
// capa de query (resolveQueryWindow); from/to/compareFrom/compareTo aquí
// siguen siendo días de calendario completos, nunca recortados a mano.
// rangePreset y comparePreset se persisten para que pickerStateFromFilter
// pueda reabrir con los presets correctos en vez de caer siempre en 'custom'.
const filterPatchFromApplied = (selection: AppliedSelection): Partial<Filter> => ({
  from: toJsDate(selection.a.start, 'start'),
  to: toJsDate(selection.a.end, 'end'),
  compareFrom: toJsDate(selection.b.start, 'start'),
  compareTo: toJsDate(selection.b.end, 'end'),
  cutoff: selection.cutoff,
  comparePreset: selection.comparePreset,
  rangePreset: selection.rangePreset
});

// Texto compacto del disparador — mismo formato que el resumen del footer,
// sin el sufijo de longitud/corte (eso es detalle de dentro del panel).
const triggerLabel = (filter: Filter, formatRangeReadout: (range: DateRange) => string): string => {
  if(!filter.from || !filter.to) {
    return t('datePicker.selectDates');
  }
  const a: DateRange = { start: toLocalDate(filter.from), end: toLocalDate(filter.to) };
  if(!filter.compareFrom || !filter.compareTo) {
    return formatRangeReadout(a);
  }
  const b: DateRange = { start: toLocalDate(filter.compareFrom), end: toLocalDate(filter.compareTo) };
  return `${formatRangeReadout(a)} ${t('common.vs')} ${formatRangeReadout(b)}`;
};

export { toLocalDate, toJsDate, pickerStateFromFilter, filterPatchFromApplied, triggerLabel };
